import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import HelpGuidePanel from "./help/HelpGuidePanel";

export default function HelpDrawer({
  open,
  onClose,
  title = "Help Guide",
  intro = "",
  sections = [],
  troubleshooting = [],
  onJumpToSection,
  layout = "drawer",
  footer = null,
  returnFocusRef = null,
  usePortal = false,
}) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current =
      returnFocusRef?.current || document.activeElement;

    const originalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusCloseButton = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const getFocusableElements = () => {
      const dialog = dialogRef.current;
      if (!dialog) return [];
      return Array.from(
        dialog.querySelectorAll(
          'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [contenteditable], [tabindex]:not([tabindex="-1"])'
        )
      );
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !dialogRef.current?.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusCloseButton);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalBodyOverflow;
      const returnFocusTarget =
        returnFocusRef?.current || previousFocusRef.current;
      if (returnFocusTarget && typeof returnFocusTarget.focus === "function") {
        returnFocusTarget.focus();
      }
    };
  }, [open, onClose, returnFocusRef]);

  if (!open) return null;

  const panel = (
    <HelpGuidePanel
      title={title}
      intro={intro}
      sections={sections}
      troubleshooting={troubleshooting}
      onClose={onClose}
      onJumpToSection={onJumpToSection}
      layout={layout}
      footer={footer}
      dialogRef={dialogRef}
      closeButtonRef={closeButtonRef}
    />
  );

  if (usePortal && typeof document !== "undefined") {
    return createPortal(panel, document.body);
  }

  return panel;
}
